import { useState } from 'react';
import { Button } from './Button';
import type { CreateTableConfig } from '../lib/gameClient';

export interface CreateGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (config: CreateTableConfig) => void;
  creating?: boolean;
}

export function CreateGameModal({
  isOpen,
  onClose,
  onCreate,
  creating = false,
}: CreateGameModalProps) {
  const [maxSeats, setMaxSeats] = useState<2 | 6>(6);
  const [smallBlindStr, setSmallBlindStr] = useState('0.25');
  const [bigBlindStr, setBigBlindStr] = useState('0.50');
  const [minBuyInStr, setMinBuyInStr] = useState('10');
  const [maxBuyInStr, setMaxBuyInStr] = useState('50');

  if (!isOpen) return null;

  const parseDollarsToCents = (s: string): number => Math.round(parseFloat(s || '0') * 100);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const sb = parseDollarsToCents(smallBlindStr);
    const bb = parseDollarsToCents(bigBlindStr);
    const min = parseDollarsToCents(minBuyInStr);
    const max = parseDollarsToCents(maxBuyInStr);

    if (sb <= 0 || bb <= 0 || min <= 0 || max <= 0) return;
    if (bb < sb) return;
    if (min > max) return;

    onCreate({
      maxSeats,
      smallBlind: sb,
      bigBlind: bb,
      minBuyIn: min,
      maxBuyIn: max,
    });
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-surface border border-white/10 text-text focus:outline-none focus:ring-2 focus:ring-primary/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-white/10 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-text">Create Game</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-text-muted text-sm mb-1.5">Seats</label>
            <div className="flex gap-2">
              {([2, 6] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setMaxSeats(n)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    maxSeats === n
                      ? 'bg-primary text-white'
                      : 'bg-white/5 text-text-muted hover:bg-white/10 hover:text-text'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-text-muted text-sm mb-1.5">Small Blind ($)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 0.25 or 25"
                value={smallBlindStr}
                onChange={(e) => setSmallBlindStr(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-text-muted text-sm mb-1.5">Big Blind ($)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 0.50 or 50"
                value={bigBlindStr}
                onChange={(e) => setBigBlindStr(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-text-muted text-sm mb-1.5">Min Buy-in ($)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 10"
                value={minBuyInStr}
                onChange={(e) => setMinBuyInStr(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-text-muted text-sm mb-1.5">Max Buy-in ($)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 50"
                value={maxBuyInStr}
                onChange={(e) => setMaxBuyInStr(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="flex-1" disabled={creating}>
              {creating ? 'Creating…' : 'Create & Play'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
